import React, { useContext } from 'react';
import styled from 'styled-components';
import { tableHelper } from '@builttocreate/engine-utils';

import FieldSettingsChartField from './FieldSettingsChartField';
import FieldSettingsImageField from './FieldSettingsImageField';
import FieldSettingsMultiSelectField from './FieldSettingsMultiSelectField';
import FieldSettingsSignatureField from './FieldSettingsSignatureField';
import FieldSettingsTextField from './FieldSettingsTextField';
import FieldSettingsBlockField from './FieldSettingsBlockField';
import FieldSettingsTableField from './FieldSettingsTableField';
import FieldSettingsTableColumn from './FieldSettingsTableColumn';
import FieldSettingsTableCell from './FieldSettingsTableCell';

import FieldTypes from '../../../constants/FieldTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';

import ThemeContext from '../../../theme/ThemeContext';
import { getFieldThemeObject } from '../../../theme/getThemeObject';

const { getTargetColumnWithOperand } = tableHelper;

const StyledFieldWrapper = styled.div`
  margin-top: 5px;
`;

const FieldSettings = (props) => {

  const {
    _id,
    field,
    identifier,
    type,
    displayType,
    columnType,
    options,
    onChange,
    fieldDefaultStyles,
    config = {},
    mode, //SDK Mode
    fieldMode //Page Field Presentation
  } = props;

  /**
   * Step 1: Settings Configuration
   */
  const fieldSettingsConfig = config[identifier] || config[`${type}Field`] || config[type] || config?.field;
  const columnSettingsConfig = fieldSettingsConfig?.tableColumns;

  /**
   * Step 2: Theming and Field Styles
   */
  const theme = useContext(ThemeContext);
  const fieldTheme = getFieldThemeObject({
    mode,
    fieldPresentationMode: fieldMode,
    fieldPosition: props,
    fileStyles: fieldDefaultStyles,
    theme: theme?.field,
    sdkTheme: theme
  });

  /**
   * Step 3: Set field props
   *
   * <!---------------NOTICE---------------------->
   *  Props should be passed after fieldTheme in the fieldProps object in order to pass the latest
   *   changes that were made via field settings.
   */

  const fieldProps = {
    fieldPositionId: _id,
    fieldId: field,
    fieldIdentifier: identifier,
    onOptionCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => handleOptionCreate(fieldId, fieldIdentifier, fieldPositionId, options, data, onChange),
    onOptionChange: (fieldId, fieldIdentifier, fieldPositionId, optionId, data) => handleOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, options, optionId, data, onChange),
    onOptionDelete: (fieldId, fieldIdentifier, fieldPositionId, optionId) => handleOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, options, optionId, { deleted: true }, onChange),
    ...fieldTheme,
    ...props,
    config: fieldSettingsConfig || {}, //Important: Must go after "...props"
  };

  /**
   * Step 4: Render proper settings node
   */
  let fieldNode = null;

  if (type === FieldTypes.image || type === FieldTypes.file) {
    fieldNode = <FieldSettingsImageField {...fieldProps} />;
  }
  else if (type === FieldTypes.richText) {
    fieldNode = <FieldSettingsBlockField {...fieldProps} />;
  }
  else if (
    type === FieldTypes.text
    || type === FieldTypes.textarea
    || type === FieldTypes.number
    || type === FieldTypes.date
    || type === FieldTypes.uniqueId
    || type === FieldTypes.list
  ) {
    fieldNode = <FieldSettingsTextField {...fieldProps} />;
  }
  else if (type === FieldTypes.block) {
    fieldNode = <FieldSettingsBlockField {...fieldProps} />;
  }
  else if (type === FieldTypes.dropdown || type === FieldTypes.user) {
    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsMultiSelectField {...fieldProps} />
      </StyledFieldWrapper>
    );
  }
  else if (type === FieldTypes.multiSelect) {
    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsMultiSelectField {...fieldProps} />
      </StyledFieldWrapper>
    );
  }
  else if (type === FieldTypes.chart) {
    fieldNode = <FieldSettingsChartField {...fieldProps} />;
  }
  else if (type === FieldTypes.signature) {
    fieldNode = <FieldSettingsSignatureField {...fieldProps} />;
  }
  else if (type === FieldTypes.table && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.inputGroup)) {
    fieldNode = <FieldSettingsTableField {...fieldProps} />;
  }
  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.text) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
      />
    );

  }
  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.image) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual  table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        maxImageHeight={targetColumn.maxImageHeight}
        maxImageWidth={targetColumn.maxImageWidth}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
      />
    );

  }
  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.dropdown) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsTableCell
          {...fieldProps}
          columnIdentifier={targetColumn.identifier}
          columnType={columnType}
          config={fieldSettingsConfig}
          columnConfig={columnSettingsConfig}
          rows={fieldProps.value}
          columnOperand={targetColumn.operand}
          options={targetColumn.options}
          metadata={targetColumn.metadata}
          onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
          onOptionCreate={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnOptionCreate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
          onOptionChange={(fieldId, fieldIdentifier, fieldPositionId, optionId, data) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, optionId, data, onChange)}
          onOptionDelete={(fieldId, fieldIdentifier, fieldPositionId, optionId) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, optionId, { deleted: true }, onChange)}
        />
      </StyledFieldWrapper>
    );

  }

  return fieldNode;

};

export const TableColumnSettings = (props) => {

  const { _id, field, identifier, tableColumns, tableColumnOrder, columnId, onChange, onColumnIdChange, config, onFieldColumnDelete } = props;

  const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, columnId);

  const fieldSettingsConfig = config[identifier] || config[_id] || config?.field;
  const columnSettingsConfig = fieldSettingsConfig?.tableColumns;

  const fieldProps = {

    ...props,
    ...targetColumn,

    fieldId: field,
    fieldIdentifier: identifier,
    fieldPositionId: _id,

    config: columnSettingsConfig,
    displayType: FieldDisplayTypes.original,
    columnType: targetColumn.type,
    columnOperand: targetColumn.operand,
    value: targetColumn.value || '',
    onChange: (fieldId, fieldIdentifier, fieldPositionId, data) => onChange(fieldId, fieldIdentifier, fieldPositionId, data),
    onOptionCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnOptionCreate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, data, onChange),
    onOptionChange: (fieldId, fieldIdentifier, fieldPositionId, optionId, data) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, optionId, data, onChange),
    onOptionDelete: (fieldId, fieldIdentifier, fieldPositionId, optionId) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, optionId, { deleted: true }, onChange),
    onColumnIdChange: (activeColumnId) => onColumnIdChange(activeColumnId),
    /**
     * onFieldColumnDelete is used for
     * 1.Removing the active column settings selection and hiding the column settings panel.
     * 2.Deleting the column
    */
    onFieldColumnDelete,
  };

  return (
    <StyledFieldWrapper>
      <FieldSettingsTableColumn
        {...fieldProps}
      />
    </StyledFieldWrapper>
  );

};

export const handleColumnUpdate = (fieldId, fieldIdentifier, fieldPositionId, columns, columnId, data, onChange) => {

  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  const nextColumn = { ...column };
  if (column.options) nextColumn.options = [...column.options];

  nextColumns[columnIndex] = {
    ...nextColumn,
    ...data
  };

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns });

};

export const handleColumnOptionCreate = (fieldId, fieldIdentifier, fieldPositionId, columns, columnId, data, onChange) => {

  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  nextColumns[columnIndex] = {
    ...column,
    options: [...column.options]
  };

  nextColumns[columnIndex].options.push({
    _id: data._id,
    value: data.value || 'New Option',
    metadata: data.metadata || {},
    deleted: false,
  });

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns });

};

export const handleColumnOptionUpdate = (fieldId, fieldIdentifier, fieldPositionId, columns, columnId, optionId, data, onChange) => {

  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  nextColumns[columnIndex] = {
    ...column,
    options: [...column.options]
  };

  const optionIndex = column.options.findIndex((option) => option._id === optionId);

  nextColumns[columnIndex].options[optionIndex] = {
    ...nextColumns[columnIndex].options[optionIndex],
    ...data
  };

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns });

};

export const handleOptionCreate = (fieldId, fieldIdentifier, fieldPositionId, options, data, onChange) => {

  const nextOptions = [...options];

  const newOption = {
    _id: data._id,
    value: data.value || 'Option',
    width: data.width || 100,
    deleted: false
  };

  nextOptions.push(newOption);

  onChange(fieldId, fieldIdentifier, fieldPositionId, { options: nextOptions });

};

export const handleOptionUpdate = (fieldId, fieldIdentifier, fieldPositionId, options, optionId, data, onChange) => {

  const nextOptions = [...options];

  const optionIndex = nextOptions.findIndex((option) => option._id === optionId);

  const nextOption = { ...nextOptions[optionIndex], ...data };

  nextOptions[optionIndex] = nextOption;

  onChange(fieldId, fieldIdentifier, fieldPositionId, { options: nextOptions });

};

export default FieldSettings;
